import React from 'react';
import { useGetStudents } from '@/lib/hooks/useGetStudents';
import { useStudentSync } from '@/lib/hooks/useStudentSync';
import type { StudentProfileV1 } from '@/types/studentprofile';
import { useI18n } from '@/i18n/I18nProvider';
import { useAuth } from 'react-oidc-context';
import { StatusCard } from '@/components/StatusCard';
import { StudentCard } from './StudentCard';

export default function StudentsList() {
  const { t, locale } = useI18n();
  const auth = useAuth();
  const { students, error, isLoading, mutate } = useGetStudents();
  const isNotFoundError = error?.status === 404 || error?.title?.toLowerCase().includes('not found');
  const needsSync = Boolean(isNotFoundError);
  const isBusy = auth.isLoading || (auth.isAuthenticated && isLoading);

  const { syncStudents, isSyncing, syncError, setSyncError } = useStudentSync();

  const groupedChildren = React.useMemo(() => {
    if (!students || students.length === 0) {
      return { active: [], inactive: [] };
    }

    const active: StudentProfileV1[] = [];
    const inactive: StudentProfileV1[] = [];

    students.forEach((s) => {
      if (s.isActive) {
        active.push(s);
      } else {
        inactive.push(s);
      }
    });

    return { active, inactive };
  }, [students]);

  const handleSync = React.useCallback(async () => {
    try {
      await syncStudents();
      await mutate();
    } catch (err: any) {
      const errorCode = err?.error || err?.status;
      const isNotFoundError =
        errorCode === 'STUDENT_NOT_FOUND' ||
        err?.status === 404 ||
        err?.message?.toLowerCase().includes('no student found');

      if (isNotFoundError) {
        setSyncError(
          locale === 'ar'
            ? 'لم يتم العثور على بيانات الطلاب المرتبطة بحسابك. يرجى التواصل مع المدرسة للتحقق من ربط حسابك بسجلات الطلاب.'
            : 'No student records found linked to your account. Please contact your school to verify your account is properly linked to student records.'
        );
      } else {
        const errorMessage =
          err?.message || (typeof err?.error === 'string' ? err.error : null) || 'Failed to sync data';
        setSyncError(errorMessage);
      }
    }
  }, [syncStudents, mutate, setSyncError, locale]);

  return (
    <div className="space-y-4">
      {/* Loading State */}
      {isBusy && (!students || students.length === 0) && (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="rounded-xl border bg-card p-4 sm:p-5 animate-pulse">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-full bg-muted" />
                  <div className="space-y-2">
                    <div className="h-5 w-36 rounded bg-muted" />
                    <div className="h-4 w-28 rounded bg-muted/70" />
                  </div>
                </div>
                <div className="h-9 w-28 rounded-lg bg-muted" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Error / Sync State */}
      {(error || syncError) && (
        <StatusCard
          variant={needsSync ? 'default' : 'error'}
          message={
            needsSync
              ? locale === 'ar'
                ? 'مرحباً بك!'
                : 'Welcome!'
              : locale === 'ar'
                ? 'حدث خطأ'
                : 'Something went wrong'
          }
          description={
            syncError
              ? syncError
              : needsSync
                ? locale === 'ar'
                  ? 'نحتاج إلى جلب بيانات أطفالك للمرة الأولى.'
                  : "We need to fetch your children's data for the first time."
                : error instanceof Error
                  ? error.message
                  : String(error)
          }
        >
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 transition-all shadow disabled:opacity-50 inline-flex items-center gap-2"
          >
            {isSyncing
              ? locale === 'ar'
                ? 'جاري المزامنة...'
                : 'Syncing...'
              : needsSync
                ? locale === 'ar'
                  ? 'مزامنة البيانات'
                  : 'Sync Profile'
                : locale === 'ar'
                  ? 'إعادة المحاولة'
                  : 'Try Again'}
          </button>
        </StatusCard>
      )}

      {/* Active Students */}
      {groupedChildren.active.length > 0 && (
        <div className="space-y-3">
          {groupedChildren.inactive.length > 0 && (
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1 h-4 bg-emerald-500 rounded-full" />
              <h3 className="text-xs font-bold text-emerald-700 uppercase tracking-wide">
                {locale === 'ar' ? 'الطلاب النشطون' : 'Active Students'} ({groupedChildren.active.length})
              </h3>
            </div>
          )}
          {groupedChildren.active.map((student) => (
            <StudentCard key={student.id} student={student} locale={locale} t={t} />
          ))}
        </div>
      )}

      {/* Inactive Students */}
      {groupedChildren.inactive.length > 0 && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-4 bg-slate-400 rounded-full" />
            <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wide">
              {locale === 'ar' ? 'الطلاب غير النشطين' : 'Inactive Students'} ({groupedChildren.inactive.length})
            </h3>
          </div>
          {groupedChildren.inactive.map((student) => (
            <StudentCard key={student.id} student={student} locale={locale} t={t} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {auth.isAuthenticated && !isBusy && !error && (!students || students.length === 0) && (
        <StatusCard message={t.noStudentsFound} description={t.noLinkedStudents} />
      )}
    </div>
  );
}
